import { useEffect, useState } from "react";
import { Outlet, Route, Routes, Navigate, useNavigate, useLocation, matchPath } from "react-router-dom";
import { useIsOpenSidebar, useIsOpenSidebarAction } from "../context/toggleSidebarContext";
import { useAuth } from "../context/AuthContext";
import { useSweetAlert } from "../context/sweetAlertContext";
import Header from "../components/Header";
import Footer from "../components/Footer";
import Sidebar from "../components/Sidebar";
import { panelRoutes } from "../routes";
import { me, userShowMessage } from "../services";
import Dashboard from "../pages/Dashboard";
import PermissionProvider from "../context/PermissionContext";
import Restricted from "../context/Restricted";
import Loading from "../components/Loading";
import AdminLayoutPublic from "./adminLayoutPublic";

export default function AdminLayout({ children }) {
    const isShowSidebar = useIsOpenSidebar();
    const setIsOpenSidebar = useIsOpenSidebarAction();
    const navigate = useNavigate();
    const { user, isLoading } = useAuth();
    const sweetAlert = useSweetAlert();
    const [notificationList, setNotificationList] = useState([]);
    const { pathname, search } = useLocation();

    // بررسی اینکه آیا صفحه جاری پابلیک است یا خیر
    const isPublicRoute = panelRoutes.some(route =>
        route.isPublic && matchPath({ path: route.path, end: true }, pathname)
    );

    useEffect(() => {
        const currentTitle = panelRoutes.find(item => item.path === pathname);
        if (currentTitle && currentTitle.title) {
            document.title = currentTitle.title;
        }

        if (isShowSidebar?.expanded) {
            setIsOpenSidebar({ ...isShowSidebar, expanded: false });
        }
    }, [pathname, search]);

    useEffect(() => {
        const handleOffline = () => {
            sweetAlert({
                show: true,
                title: "قطع ارتباط",
                icon: "error",
                text: "اتصال شما با اینترنت قطع شد.",
                confirmButtonText: "بارگذاری مجدد",
                onConfirm: () => window.location.reload()
            });
        };
        window.addEventListener("offline", handleOffline);
        return () => window.removeEventListener("offline", handleOffline);
    }, []);

    useEffect(() => {
        const html = document.querySelector('html');
        if (isShowSidebar?.expanded) {
            html.className = 'light-style layout-navbar-fixed layout-menu-fixed layout-menu-expanded';
        } else if (isShowSidebar?.collapsed && !isShowSidebar?.hovered) {
            html.className = 'light-style layout-navbar-fixed layout-menu-fixed layout-menu-collapsed';
        } else if (isShowSidebar?.collapsed && isShowSidebar?.hovered) {
            html.className = 'light-style layout-navbar-fixed layout-menu-fixed layout-menu-collapsed layout-menu-hover';
        } else {
            html.className = 'light-style layout-navbar-fixed layout-menu-fixed';
        }
    }, [isShowSidebar]);

    const loadUser = async () => {
        try {
            const { userInfo, notifications } = await me();
            if (userInfo?.isSuccess) {
                setNotificationList(notifications);
            }
        } catch (ex) {}
    };

    useEffect(() => {
        if (user) {
            loadUser();
        }
    }, [user]);

    const closeSidebar = () => {
        setIsOpenSidebar({ ...isShowSidebar, expanded: false });
    };

    const getMessage = async (id = null) => {
        if (!user?.userPermission?.some(x => x === "MessageUserShowMessage")) return;
        try {
            const values = { id };
            const { data, status } = await userShowMessage(values);
            if (status === 200) {
                const newNotifications = [...notificationList];
                const item = newNotifications.find(x => x.id === data.id);
                if (item) {
                    item.seen = true;
                    setNotificationList(newNotifications);
                }
                sweetAlert({
                    show: true,
                    text: `${data.text}`,
                    confirmButtonText: "مشاهده شد",
                    marginTop: "20px",
                    onConfirm: () => {
                        if (id == null) getMessage();
                    }
                });
            }
        } catch (ex) {}
    };

    if (isLoading) {
        return <Loading />;
    }

    // نکته کلیدی: اگر لاگین نبود:
    if (!user) {
        // الف) اگر روت پابلیک است، به صورت خودکار با AdminLayoutPublic رندر شود
        if (isPublicRoute) {
            return <AdminLayoutPublic>{children}</AdminLayoutPublic>;
        }
        // ب) اگر روت خصوصی بود، برود لاگین
        return <Navigate to="/login" replace />;
    }

    return (
        <PermissionProvider permissions={user?.userPermission}>
            <div className="layout-wrapper layout-content-navbar">
                <div className="layout-container">
                    {user?.userInfo?.data?.template !== "NotMenu" ? <Sidebar /> : null}

                    <div className={`layout-page ${user?.userInfo?.data?.template === "NotMenu" ? "pt-custom-0" : ""}`}>
                        {user?.userInfo?.data?.template !== "NotMenu" ? (
                            <Header notifications={notificationList} getMessage={getMessage} />
                        ) : null}

                        <div className="content-wrapper">
                            <div className="container-xxl flex-grow-1 container-p-y">
                                <Routes>
                                    {panelRoutes.map(route => (
                                        <Route
                                            key={route.id}
                                            path={route.path}
                                            element={
                                                route.isPublic ? (
                                                    <route.component getMessage={getMessage} />
                                                ) : (
                                                    <Restricted to={route.permission} replace={true}>
                                                        <route.component getMessage={getMessage} />
                                                    </Restricted>
                                                )
                                            }
                                        />
                                    ))}

                                    <Route path="/*" element={<Dashboard />} />
                                </Routes>

                                <Outlet />
                                {children}
                            </div>
                            {user?.userPermission?.some(x => x === "UserDashboard") ? <Footer /> : null}
                            <div className="content-backdrop fade" />
                        </div>
                    </div>
                </div>

                <div className="layout-overlay layout-menu-toggle" onClick={closeSidebar} />
                <div className="drag-target h-100-px" />
            </div>
        </PermissionProvider>
    );
}
